import { Footer } from "@app/shared/components/Footer";
import { Header } from "@app/shared/components/Header";
import { BottomNavigation } from "@app/shared/components/BottomNavigation/BottomNavigation";
import { PortalsWrapper } from "@design-system/components/Portals";
import { ToastConfirm } from "@design-system/components/toast";
import { Toaster } from "@design-system/ui/sonner";
import { SubscribeToNewsletter } from "../../components/SubscribeToNewsletter";
import { useIsFullScreenLayout } from "../FullScreenLayout";

type Props = {
  children: React.ReactNode;
  pathname: string;
};

/**
 * Base layout can be used to wrap all pages
 */
export function BaseLayout({ children, pathname }: Props) {
  // A page in progress that owns the whole screen (a live quiz attempt):
  // the site chrome is not rendered at all — not just hidden — so nothing of
  // it can be focused. The `<main>` keeps its place in the tree, so the page
  // never remounts when the chrome comes back.
  const isFullScreen = useIsFullScreenLayout();

  if (pathname.includes("/auth") && !pathname.endsWith("/auth")) {
    return (
      <>
        <ToastConfirm />
        <Toaster />
        <PortalsWrapper />
        <main>{children}</main>
      </>
    );
  }

  // Shorts pages — full-bleed, no Header / Footer / Newsletter / BottomNav.
  // Matches the YouTube Shorts UX where the player owns the viewport.
  // Covers `/shorts`, `/shorts/<idOrSlug>`, and the locale-prefixed variants
  // (`/ar/shorts`, `/en/shorts`, …).
  if (/^\/(?:[a-z]{2}\/)?shorts(?:\/|$)/.test(pathname)) {
    return (
      <>
        <ToastConfirm />
        <Toaster />
        <PortalsWrapper />
        <main>{children}</main>
      </>
    );
  }

  return (
    <div className="app-shell">
      {!isFullScreen && <Header />}
      <ToastConfirm />
      <Toaster />
      <main
        className={
          isFullScreen
            ? "app-shell__content min-h-screen"
            : "app-shell__content pb-20 lg:pb-0"
        }
      >
        {children}
      </main>
      {!isFullScreen && <SubscribeToNewsletter />}
      <PortalsWrapper />
      {!isFullScreen && <Footer />}
      {!isFullScreen && <BottomNavigation />}
    </div>
  );
}
